import * as React from 'react';
import { Avatar, Chip, Theme, createStyles, makeStyles } from '@material-ui/core';
import { FormattedDateTime, DateTimeDisplayStyle } from '../../components/DataDisplay/FormattedDateTime';
import { Message } from './hooks/useMessageQuery';
import { AppUser } from '../../hooks/useCurrentUser';
import { SourceMessage } from '../../common/constants';

// Plain CSS colour names (not a Semantic UI dependency - SemanticCOLORS was only ever used as
// a type annotation on this same array of literal strings, never a rendered component), picked
// by a stable hash of the sender's name so the same person always gets the same avatar colour.
const AVATAR_COLORS = ['#d32f2f', '#ef6c00', '#827717', '#2e7d32', '#00796b', '#1565c0', '#6a1b9a', '#ad1457', '#4e342e', '#616161'];

function hashString(input: string): number {
    var hash = 0, i, chr;
    if (input) {
        if (input.length === 0) {
            return hash;
        }
        for (i = 0; i < input.length; i++) {
            chr = input.charCodeAt(i);
            // tslint:disable-next-line:no-bitwise
            hash = ((hash << 5) - hash) + chr - 1;
            // tslint:disable-next-line:no-bitwise
            hash |= 0; // Convert to 32bit integer
        }
    }
    return hash;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: {
            display: 'flex',
            gap: theme.spacing(1.5),
            marginBottom: theme.spacing(2.5),
            '&:last-child': {
                marginBottom: 0,
            },
        },
        avatar: {
            width: 40,
            height: 40,
            fontSize: 14,
            fontWeight: 700,
            flexShrink: 0,
        },
        column: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            maxWidth: '75%',
        },
        senderRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.75),
            marginBottom: 4,
        },
        senderName: {
            fontSize: 12.5,
            fontWeight: 700,
            color: theme.palette.text.primary,
        },
        internalChip: {
            height: 18,
            fontSize: 10.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            backgroundColor: 'var(--rv-accent-orange-bg)',
            color: 'var(--rv-accent-orange)',
        },
        bubble: {
            borderRadius: 12,
            padding: theme.spacing(1.25, 1.75),
            fontSize: 13.5,
            lineHeight: 1.5,
            color: theme.palette.text.primary,
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
            border: '1px solid transparent',
        },
        bubbleFirm: {
            backgroundColor: 'var(--rv-accent-indigo-bg)',
            borderColor: 'rgba(0,0,0,0.04)',
        },
        bubbleExternal: {
            backgroundColor: theme.palette.background.paper,
            borderColor: 'var(--rv-accent-teal)',
        },
        timestamp: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            marginTop: 4,
        },
    })
);

export interface MessageCardProps {
    data: Message;
    currentUser: AppUser | null;
}

export const RvMessageCard: React.FC<MessageCardProps> = (props) => {
    const classes = useStyles();
    const { data, currentUser } = props;

    const stringName = data.enteredBy ? data.enteredBy : '';
    const avatarColor = AVATAR_COLORS[Math.abs(hashString(stringName)) % AVATAR_COLORS.length];
    const stringInitials = stringName.split(' ').map((n) => n[0]).join('').slice(0, 2);
    const body = `${data.subject ? data.subject : ''}${data.subject ? ' - ' : ''}${data.comment}`;

    const isMessageForCurrentUser =
        !!currentUser && !!data.enteredByUserId && currentUser.userId.toLocaleLowerCase() === data.enteredByUserId.toLocaleLowerCase();
    const isInternalOnlyMessage = !!currentUser?.isInternal && data.source === SourceMessage.InternalMessageOnlyFromInternalUser;
    // Matches the original MessageDetails className={data.source ? 'ExternalUser' : 'Firm'}
    // exactly - ExternalMessageToClient (0, falsy) is the only "Firm" style; both
    // InternalMessageOnlyFromInternalUser (1) and ExternalMessageFromClient (2) get the
    // "external" style. Preserved as-is rather than "corrected" - a styling reskin, not a
    // logic change.
    const isExternalStyled = !!data.source;

    return (
        <div className={classes.row} style={{ flexDirection: isMessageForCurrentUser ? 'row-reverse' : 'row' }}>
            <Avatar className={classes.avatar} style={{ backgroundColor: avatarColor, color: '#fff' }}>
                {stringInitials}
            </Avatar>
            <div className={classes.column} style={{ alignItems: isMessageForCurrentUser ? 'flex-end' : 'flex-start' }}>
                <div className={classes.senderRow}>
                    <span className={classes.senderName}>{data.enteredBy}</span>
                    {isInternalOnlyMessage && <Chip label="Internal" size="small" className={classes.internalChip} />}
                </div>
                <div className={`${classes.bubble} ${isExternalStyled ? classes.bubbleExternal : classes.bubbleFirm}`}>
                    {body}
                </div>
                <span className={classes.timestamp}>
                    <FormattedDateTime
                        value={data.date}
                        displayStyle={DateTimeDisplayStyle.ShortDateTime}
                        DisplayInUTC={false}
                    />
                </span>
            </div>
        </div>
    );
};
